import { Col, Row } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import moment from "moment";
import { formatScheduleTime } from "helpers/multistopHelpers";

const DATE_FORMAT = "YYYY-MM-DD, HH:mm";

function StopSection({ stop, index, schedule }: any) {
  const intl = useIntl();
  const isPickup = stop.stop_type === "Pickup";
  const { address_details } = stop;

  const stopFormData = schedule?.stops?.[index];
  const scheduledDate = stopFormData?.quoted_date;
  const scheduledTime = stopFormData?.quoted_time;
  const scheduledDateTime =
    scheduledDate && scheduledTime
      ? moment(`${scheduledDate} ${scheduledTime}`, "YYYY-MM-DD HH:mm:ss").format(DATE_FORMAT)
      : "-";

  const requestedTime = formatScheduleTime({
    stop_after_datetime: stop.stop_after_datetime,
    stop_before_datetime: stop.stop_before_datetime,
    stop_time_type: stop.stop_time_type,
    stop_timezone: stop.stop_timezone,
    intl,
  });

  const contactParts = [stop.stop_contact_name, stop.stop_contact_number, stop.stop_contact_email].filter(Boolean);

  return (
    <div className="mb-3xl">
      <p className="font-b4 text-secondary font-weight-medium mb-2">
        <FormattedMessage id="quotation-form.stop" defaultMessage="Stop" /> #{index + 1} -{" "}
        {isPickup ? (
          <FormattedMessage id="general.pickup" defaultMessage="Pickup" />
        ) : (
          <FormattedMessage id="general.delivery" defaultMessage="Delivery" />
        )}
      </p>

      <div className="font-b3 space-y-2">
        <Row>
          <Col className="text-secondary">
            <FormattedMessage id="quote-request-detail.scheduledTime" defaultMessage="Scheduled time" />
          </Col>
          <Col>{scheduledDateTime}</Col>
        </Row>

        <Row>
          <Col className="text-secondary">
            <FormattedMessage id="quote-request-detail.requestedTime" defaultMessage="Requested time" />
          </Col>
          <Col className="text-secondary">{requestedTime}</Col>
        </Row>

        {address_details?.name && (
          <Row>
            <Col className="text-secondary">
              <FormattedMessage id="quote-request-detail.company" defaultMessage="Company" />
            </Col>
            <Col>{address_details.name}</Col>
          </Row>
        )}

        {address_details?.street_address && (
          <Row>
            <Col className="text-secondary">
              <FormattedMessage id="general.address" defaultMessage="Address" />
            </Col>
            <Col>
              {address_details.street_address}
              {address_details.street_address_line_two ? `, ${address_details.street_address_line_two}` : ""}
            </Col>
          </Row>
        )}

        <Row>
          <Col className="text-secondary">
            <FormattedMessage id="quote-request-detail.cityAndPostalCode" defaultMessage="City and postal code" />
          </Col>
          <Col>
            {[address_details?.state, address_details?.city].filter(Boolean).join(", ")}
            {address_details?.zipcode ? `, ${address_details.zipcode}` : ""}
          </Col>
        </Row>

        <Row>
          <Col className="text-secondary">
            <FormattedMessage id="quote-request-detail.stateAndCountry" defaultMessage="State and country" />
          </Col>
          <Col>{[address_details?.zipcode, address_details?.country].filter(Boolean).join(" ")}</Col>
        </Row>

        {contactParts.length > 0 && (
          <Row>
            <Col className="text-secondary">
              <FormattedMessage id="general.contact" defaultMessage="Contact" />
            </Col>
            <Col>{contactParts.join(" | ")}</Col>
          </Row>
        )}

        <Row>
          <Col className="text-secondary">
            {isPickup ? (
              <FormattedMessage id="general.pickupInstructions" defaultMessage="Pickup instructions" />
            ) : (
              <FormattedMessage id="general.deliveryInstructions" defaultMessage="Delivery instructions" />
            )}
          </Col>
          <Col>{stop.stop_notes || "-"}</Col>
        </Row>
      </div>
    </div>
  );
}

interface MultiStopScheduleSummaryProps {
  stops: any[];
  schedule: any;
  isOceanFreight: boolean;
  isAirFreight: boolean;
}

export default function MultiStopScheduleSummary({
  stops,
  schedule,
  isOceanFreight,
  isAirFreight,
}: MultiStopScheduleSummaryProps) {
  return (
    <>
      {stops.map((stop: any, index: number) => (
        <StopSection key={index} stop={stop} index={index} schedule={schedule} />
      ))}

      {isOceanFreight && (schedule?.vessels || []).length > 0 && (
        <>
          <hr className="my-4" />
          {(schedule.vessels as any[]).map((vessel: any, vIndex: number) => (
            <div key={vIndex} className="space-y-2 mb-3xl">
              <p className="font-b3">
                <FormattedMessage id="general.vessel" defaultMessage="Vessel" /> {vIndex + 1}
              </p>
              <Row>
                <Col className="text-secondary">
                  <FormattedMessage id="quote.provider.vesselNumber" defaultMessage="Vessel number / IMO" />
                </Col>
                <Col>{vessel.vessel_number || "-"}</Col>
              </Row>
              <Row>
                <Col className="text-secondary">
                  <FormattedMessage id="quote.provider.vesselDepartureDate" defaultMessage="Vessel departure date" />
                </Col>
                <Col>{vessel.delivery_date ? moment(vessel.delivery_date, "YYYY-MM-DD").format("DD MMM YYYY") : "-"}</Col>
              </Row>
              <Row>
                <Col className="text-secondary">
                  <FormattedMessage id="quote.provider.departurePort" defaultMessage="Departure port" />
                </Col>
                <Col>
                  {[vessel.departure_port_code, vessel.departure_city, vessel.departure_country_name]
                    .filter(Boolean)
                    .join(", ") || "-"}
                </Col>
              </Row>
              <Row>
                <Col className="text-secondary">
                  <FormattedMessage id="quote.provider.vesselArrivalDate" defaultMessage="Vessel arrival date" />
                </Col>
                <Col>
                  {vessel.vessel_arrival_date ? moment(vessel.vessel_arrival_date, "YYYY-MM-DD").format("DD MMM YYYY") : "-"}
                </Col>
              </Row>
              <Row>
                <Col className="text-secondary">
                  <FormattedMessage id="quote.provider.arrivalPort" defaultMessage="Arrival port" />
                </Col>
                <Col>
                  {[vessel.arrival_port_code, vessel.arrival_city, vessel.arrival_country_name].filter(Boolean).join(", ") ||
                    "-"}
                </Col>
              </Row>
            </div>
          ))}
        </>
      )}

      {isAirFreight && (schedule?.flights || []).length > 0 && (
        <>
          <hr className="my-4" />
          {(schedule.flights as any[]).map((flight: any, fIndex: number) => (
            <div key={fIndex} className="space-y-2 mb-3xl">
              <p className="font-b3">
                <FormattedMessage id="quotation-form.flight" defaultMessage="Flight" /> {fIndex + 1}
              </p>
              <Row>
                <Col className="text-secondary">
                  <FormattedMessage id="quote.provider.schedule.airline" defaultMessage="Airline" />
                </Col>
                <Col>
                  {flight?.airline_code}
                  {flight?.flight_number ? `, ${flight.flight_number}` : ""}
                </Col>
              </Row>
              <Row>
                <Col className="text-secondary">
                  <FormattedMessage id="quote.provider.schedule.departureDate" defaultMessage="Departure date" />
                </Col>
                <Col>
                  {flight?.departure_date
                    ? moment(`${flight.departure_date} ${flight.departure_time}`, "YYYY-MM-DD HH:mm:ss").format(DATE_FORMAT)
                    : "-"}
                </Col>
              </Row>
              <Row>
                <Col className="text-secondary">
                  <FormattedMessage id="quote.provider.schedule.departureAirport" defaultMessage="Departure airport" />
                </Col>
                <Col>{flight?.departure_airport_code || "-"}</Col>
              </Row>
              <Row>
                <Col className="text-secondary">
                  <FormattedMessage id="quote.provider.schedule.arrivalDate" defaultMessage="Arrival date" />
                </Col>
                <Col>
                  {flight?.arrival_date
                    ? moment(`${flight.arrival_date} ${flight.arrival_time}`, "YYYY-MM-DD HH:mm:ss").format(DATE_FORMAT)
                    : "-"}
                </Col>
              </Row>
              <Row>
                <Col className="text-secondary">
                  <FormattedMessage id="quote.provider.arrivalAirport" defaultMessage="Arrival airport" />
                </Col>
                <Col>{flight?.arrival_airport_code || "-"}</Col>
              </Row>
            </div>
          ))}
        </>
      )}
    </>
  );
}
